import { FormElementChangeEvent, OptionProps } from '..';

import { get, omit } from 'lodash';
import { Col } from 'antd';
import React, { useCallback } from 'react';
import moment from 'moment';

import Panel, { PanelProps } from '.';

import FormItem from '../form-item';
import { getLocalTimezone, getCombinedDateStr, getValueWithoutTimezone } from '../../../utils/date';
import { DATETIME_SCHEMA_TEMPLATE } from '../../../utils/form-builder';
import data from '../../../resources/data/timezones.json';

const zones = data['timezones'].map(item => item.value).sort();

const localTimezone = getLocalTimezone();
const DEFAULT_ZONE_VALUE = localTimezone?.value;
const FORMAT_WITHOUT_TIMEZONE = 'YYYY-MM-DDTHH:mm:ss';

export enum FieldKind {
  Date,
  Timezone,
}

export function getLayoutProps() {
  return { span: 24 };
}

function getFieldNameByFieldKind(kind: FieldKind) {
  return get(DATETIME_SCHEMA_TEMPLATE, `elements[${kind}].name`);
}

function toISOString(dateStr?: string) {
  if (!dateStr) {
    return dateStr;
  }
  return moment(dateStr).toISOString();
}

const PanelDateTime = React.memo(function PanelDateTime({
  elements,
  value: panelValue,
  disabled,
  repeatable,
  ...props
}: PanelProps) {
  const fieldRenderer = useCallback(
    (_: string, attributes: any, error: any, Comp: React.ComponentType<any>, index = 0) => {
      const { name, label, value: fieldValue, itemIndex } = attributes;
      const componentProps = omit(attributes, ['instructiveLabel', 'itemIndex']);
      componentProps.disabled = disabled;
      const isDateTime = index === FieldKind.Date;
      const isTimezone = index === FieldKind.Timezone;

      const timezoneFieldName = getFieldNameByFieldKind(FieldKind.Timezone);
      const timezoneValue = get(panelValue, repeatable ? `[${itemIndex}].${timezoneFieldName}` : timezoneFieldName);

      if (isTimezone) {
        // enable auto complete
        componentProps.showSearch = true;
        componentProps.filterOption = (input: string, option: React.ReactElement<OptionProps>) =>
          option &&
          option.props !== null &&
          option.props.children !== null &&
          typeof option.props.children === 'string' &&
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        componentProps.options = zones.map(timezone => ({ label: timezone, value: timezone }));

        const { value, onChange } = componentProps;
        // set default value to local timezone if the value is not set
        if (value === undefined) {
          delete componentProps.value;
          componentProps.defaultValue = DEFAULT_ZONE_VALUE;
          if (onChange) {
            onChange({ name, value: componentProps.defaultValue });
          }
        }

        componentProps.onChange = ({ name, value }: FormElementChangeEvent) => {
          const datetimeFieldName = getFieldNameByFieldKind(FieldKind.Date);
          const datetimeValue = get(panelValue, repeatable ? `[${itemIndex}].${datetimeFieldName}` : datetimeFieldName);
          if (datetimeValue) {
            // get the current date value(w/ the current timezone value) w/o timezone
            const valueWithoutTimezone = getValueWithoutTimezone(datetimeValue, timezoneValue);
            // create new ISO string with new timezone
            const val = getCombinedDateStr(valueWithoutTimezone, value);
            // set value as UTC
            onChange({ name: elements[FieldKind.Date].name, value: toISOString(val) });
          }

          // call the original `onChange`
          onChange({ name, value });
        };
      }

      if (isDateTime) {
        const { onChange } = componentProps;
        componentProps.onChange = ({ name, value }: FormElementChangeEvent) => {
          // convert moment/dayjs instance to ISO string with timezone
          const val = value && getCombinedDateStr(value.format(FORMAT_WITHOUT_TIMEZONE), timezoneValue);
          // set value as UTC
          onChange({ name, value: val && toISOString(val) });
        };
        let valueWithoutTimezone = fieldValue;
        if (fieldValue && typeof fieldValue === 'string') {
          // convert ISO string with timezone to moment/dayjs instance
          valueWithoutTimezone = getValueWithoutTimezone(fieldValue, timezoneValue);
          // set the date picker with only date and time
          componentProps.value = moment(valueWithoutTimezone);
        }
      }

      // responsive layout config
      const layoutProps = getLayoutProps();

      return (
        <Col {...layoutProps}>
          <FormItem label={label} htmlFor={componentProps.id} error={error}>
            <Comp error={error} {...componentProps} />
          </FormItem>
        </Col>
      );
    },
    [disabled, elements, panelValue, repeatable]
  );

  return (
    <Panel
      {...props}
      elements={elements}
      fieldRenderer={fieldRenderer}
      value={panelValue}
      disabled={disabled}
      repeatable={repeatable}
    />
  );
});

export default PanelDateTime;
